import { useState } from 'react';
import {
  Download,
  Truck,
  Package,
  Loader2,
  Search,
  Printer,
  MapPin,
  ArrowRight,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useInTransitReport } from '@/hooks/useInTransitReport';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { useReportPrint } from '@/hooks/useReportPrint';
import { formatDistanceToNow } from 'date-fns';

export default function InTransitReport() {
  const { locations } = useLocations();
  const { formatCurrency: _formatCurrency } = useCurrency();
  const { printRef, handlePrint } = useReportPrint({ title: 'In-Transit Report' });

  const [fromLocationId, setFromLocationId] = useState<string>('all');
  const [toLocationId, setToLocationId] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'items' | 'transfers' | 'lost'>('items');

  const {
    inTransitItems,
    lostItems,
    summary,
    groupedByTransfer,
    groupedByProduct: _groupedByProduct,
    isLoading,
    error,
    downloadCSV,
  } = useInTransitReport({
    fromLocationId: fromLocationId === 'all' ? undefined : fromLocationId,
    toLocationId: toLocationId === 'all' ? undefined : toLocationId,
  });

  // Filter items by search
  const filteredItems = searchQuery
    ? inTransitItems.filter(
        (item) =>
          item.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.product_sku && item.product_sku.toLowerCase().includes(searchQuery.toLowerCase())) ||
          item.transfer_number.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : inTransitItems;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const formatRelativeDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">In-Transit Report</h1>
          <p className="text-sm text-muted-foreground">
            Track items currently in transit between locations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={downloadCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Active Transfers</span>
              <Truck className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summary?.total_transfers || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Items In Transit</span>
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summary?.total_items || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Units In Transit</span>
              <Package className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summary?.total_units_in_transit?.toLocaleString() || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Lost Items Warning */}
      {lostItems.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <span className="font-medium text-amber-800 dark:text-amber-200">
                {lostItems.length} items were not fully received on completed transfers
              </span>
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={() => setActiveTab('lost')}
              >
                View Lost Items
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={fromLocationId} onValueChange={setFromLocationId}>
          <SelectTrigger className="w-[180px]">
            <MapPin className="h-4 w-4 mr-2" />
            <SelectValue placeholder="From Location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <ArrowRight className="h-4 w-4 text-muted-foreground" />

        <Select value={toLocationId} onValueChange={setToLocationId}>
          <SelectTrigger className="w-[180px]">
            <MapPin className="h-4 w-4 mr-2" />
            <SelectValue placeholder="To Location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Destinations</SelectItem>
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products or transfers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-center text-destructive">
            Error loading in-transit data. Please try again.
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <TabsList>
            <TabsTrigger value="items">By Item ({filteredItems.length})</TabsTrigger>
            <TabsTrigger value="transfers">By Transfer ({groupedByTransfer.length})</TabsTrigger>
            <TabsTrigger value="lost" className={lostItems.length > 0 ? 'text-amber-600' : ''}>
              Lost ({lostItems.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="items" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Items In Transit</CardTitle>
                <CardDescription>
                  All items currently being transferred between locations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead>Transfer</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead className="text-right">Sent</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                      <TableHead className="text-right">In Transit</TableHead>
                      <TableHead>Shipped</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item, idx) => (
                      <TableRow key={`${item.transfer_id}-${item.product_id}-${idx}`}>
                        <TableCell>
                          <Badge variant="outline">{item.transfer_number}</Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{item.product_name}</div>
                            {item.product_sku && (
                              <div className="text-xs text-muted-foreground">
                                {item.product_sku}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.from_location_name}
                        </TableCell>
                        <TableCell>{item.to_location_name}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {item.quantity_sent}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {item.quantity_received}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-blue-600">
                          {item.in_transit}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {formatDate(item.shipped_at)}
                            {item.shipped_at && (
                              <div className="text-xs text-muted-foreground">
                                {formatRelativeDate(item.shipped_at)}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No items currently in transit
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transfers" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Transfers In Progress</CardTitle>
                <CardDescription>Active transfers with items not yet received</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {groupedByTransfer.map((transfer) => (
                    <div
                      key={transfer.transferId}
                      className="p-4 border rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{transfer.transferNumber}</Badge>
                          <Badge
                            variant="secondary"
                            className="bg-blue-100 text-blue-800"
                          >
                            {transfer.status}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {transfer.shippedAt && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Shipped {formatRelativeDate(transfer.shippedAt)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-sm mb-3">
                        <span className="text-muted-foreground">{transfer.fromLocationName}</span>
                        <ArrowRight className="h-4 w-4" />
                        <span className="font-medium">{transfer.toLocationName}</span>
                      </div>

                      <div className="text-sm">
                        <span className="font-medium">{transfer.items.length} items</span>
                        <span className="text-muted-foreground mx-2">·</span>
                        <span className="text-blue-600">{transfer.totalInTransit} units in transit</span>
                      </div>

                      <div className="mt-2 text-xs text-muted-foreground">
                        {transfer.items.slice(0, 3).map((item) => item.product_name).join(', ')}
                        {transfer.items.length > 3 && ` +${transfer.items.length - 3} more`}
                      </div>
                    </div>
                  ))}
                  {groupedByTransfer.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No transfers currently in progress
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="lost" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Lost In Transit
                </CardTitle>
                <CardDescription>
                  Items not fully received on completed transfers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead>Transfer</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead className="text-right">Sent</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                      <TableHead className="text-right">Lost</TableHead>
                      <TableHead>Received Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lostItems.map((item, idx) => (
                      <TableRow key={`${item.transfer_id}-${item.product_id}-${idx}`}>
                        <TableCell>
                          <Badge variant="outline">{item.transfer_number}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.from_location_name} → {item.to_location_name}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {item.quantity_sent}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {item.quantity_received}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-red-600">
                          {item.quantity_lost}
                        </TableCell>
                        <TableCell>{formatDate(item.received_at)}</TableCell>
                      </TableRow>
                    ))}
                    {lostItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No lost items found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Print template */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>In-Transit Report</h1>
            <div className="print-meta">
              {fromLocationId !== 'all' || toLocationId !== 'all'
                ? `${fromLocationId !== 'all' ? locations.find((l) => l.id === fromLocationId)?.name : 'All'} → ${toLocationId !== 'all' ? locations.find((l) => l.id === toLocationId)?.name : 'All'}`
                : 'All Locations'}
            </div>
          </div>
          <div className="print-meta">Generated on {new Date().toLocaleString()}</div>
        </div>

        <div className="print-summary">
          <div className="print-card">
            <div className="label">Active Transfers</div>
            <div className="value">{summary?.total_transfers || 0}</div>
          </div>
          <div className="print-card">
            <div className="label">Items</div>
            <div className="value">{summary?.total_items || 0}</div>
          </div>
          <div className="print-card">
            <div className="label">Units In Transit</div>
            <div className="value">{summary?.total_units_in_transit || 0}</div>
          </div>
        </div>

        <div className="print-section">
          <h2>Items In Transit</h2>
          <table className="print-table">
            <thead>
              <tr>
                <th>Transfer</th>
                <th>Product</th>
                <th>From</th>
                <th>To</th>
                <th>Sent</th>
                <th>Received</th>
                <th>In Transit</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, idx) => (
                <tr key={`${item.transfer_id}-${item.product_id}-${idx}`}>
                  <td>{item.transfer_number}</td>
                  <td>{item.product_name}</td>
                  <td>{item.from_location_name}</td>
                  <td>{item.to_location_name}</td>
                  <td>{item.quantity_sent}</td>
                  <td>{item.quantity_received}</td>
                  <td>{item.in_transit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
